import { useCallback, useEffect, useState } from "react";
import { getOrganizations } from "../utils";
import { useSession } from "../session/SessionContext";

// Shared by the sidebar and the organization list, so navigating between pages
// does not refetch. Keyed by user, so signing in as someone else starts fresh.
let cache: { userId: string; request: Promise<Organization[]> } | null = null;

const load = (userId: string, refresh = false) => {
  if (refresh || cache?.userId !== userId) {
    const request = getOrganizations() as Promise<Organization[]>;
    cache = { userId, request };
    request.catch(() => {
      if (cache?.request === request) cache = null;
    });
  }
  return cache!.request;
};

const useOrganizations = () => {
  const { user } = useSession();
  const [organizations, setOrganizations] = useState<Organization[] | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(
    (refresh: boolean) => {
      if (!user) return () => {};
      let live = true;
      load(user.id, refresh).then(
        (result) => live && setOrganizations(result),
        (e: Error) => live && setError(e),
      );
      return () => {
        live = false;
      };
    },
    [user],
  );

  useEffect(() => fetch(false), [fetch]);

  const reload = useCallback(() => {
    fetch(true);
  }, [fetch]);

  return { organizations, error, reload };
};

export default useOrganizations;
